import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Typography,
  Box
} from '@mui/material';
import { ButtonSpinnerSlot } from '../ui/ButtonSpinner';
import useDemoActionGate from '../hooks/useDemoActionGate';
import DemoLockedDialog from '../ui/DemoLockedDialog';

const FormDialog = ({ open, handleClose, data, onChange, handleFormSubmit, loading }) => {
  const { guardAction, lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();
  const onSubmit = () => {
    if (!guardAction('action.assessment.question.save')) return;
    handleFormSubmit();
  };
  const [localData, setLocalData] = useState(data || {});
  //console.log(localData)

  useEffect(() => {
    setLocalData(data || {});
  }, [data]);

  const onAnswerChange = (e, index) => {
    const updatedAnswers = [...(localData.answer || [])];
    if (updatedAnswers[index]) {
      updatedAnswers[index] = { ...updatedAnswers[index], text: e.target.value };
    }
    setLocalData({ ...localData, answer: updatedAnswers });
    // Keep the parent's formData in sync so the submit (which reads parent state)
    // includes edited incorrect answers.
    onChange({ target: { id: 'answer', value: updatedAnswers } });
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    setLocalData({ ...localData, [id]: value });
    onChange(e); // maintain parent's state
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="form-dialog-title"
      fullWidth
      maxWidth="md"
      disableScrollLock
    >
      <DialogTitle id="form-dialog-title" sx={{ fontWeight: 'bold' }}>
        {localData?.questionId ? 'Update Question' : 'Add New Question'}
      </DialogTitle>

      <DialogContent dividers sx={{ overflowY: 'auto', py: 3 }}>
        {localData.passage && (
  <Box mb={2}>
    {/^https?:\/\/.+\.(jpg|jpeg|png|gif|bmp|webp)$/.test(localData.passage) ? (
      <img
        src={localData.passage}
        alt="Case Study"
        style={{ width: '100%', borderRadius: 8, height:'400px' }}
      />
    ) : (
      <>
        <Typography variant="subtitle1" fontWeight="bold">Passage:</Typography>
        <Typography
          variant="body2"
          sx={{ whiteSpace: 'pre-wrap', backgroundColor: '#f5f5f5', p: 1, borderRadius: 1 }}
        >
          {localData.passage}
        </Typography>
      </>
    )}
  </Box>
)}

        {/* Generated questions run long (a scenario followed by the question), so
            these wrap onto multiple lines and grow instead of scrolling sideways
            in a one-line input. */}
        <TextField
          margin="dense"
          id="question"
          label="Question"
          type="text"
          fullWidth
          multiline
          minRows={3}
          maxRows={12}
          value={localData.question || ''}
          onChange={handleChange}
        />

        <TextField
          margin="dense"
          id="explanation"
          label="Explanation"
          type="text"
          fullWidth
          multiline
          minRows={2}
          maxRows={8}
          value={localData.explanation || ''}
          onChange={handleChange}
        />

        <TextField
          margin="dense"
          id="hint"
          label="Hint"
          type="text"
          fullWidth
          multiline
          minRows={2}
          maxRows={6}
          value={localData.hint || ''}
          onChange={handleChange}
        />

        <Box mt={2}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Answers
          </Typography>

          <TextField
            margin="dense"
            id="correctAnswer"
            label="Correct Answer"
            type="text"
            fullWidth
            value={localData.correctAnswer || ''}
            onChange={handleChange}
            sx={{ backgroundColor: '#92e4a9ff' }} // light green
          />

          {localData.answer &&
            localData.answer.map((answer, index) =>
              !answer.isCorrect ? (
                <TextField
                  key={`${answer.answerId ?? 'answer'}-${index}`}
                  margin="dense"
                  label={`Incorrect Answer ${index + 1}`}
                  type="text"
                  fullWidth
                  value={answer.text || ''}
                  onChange={(e) => onAnswerChange(e, index)}
                  sx={{ backgroundColor: '#f3bdb7ff' }} // light red
                />
              ) : null
            )}
        </Box>

        <TextField
          margin="dense"
          id="mimeType"
          label="Mime Type"
          type="text"
          fullWidth
          value={localData.mimeType || ''}
          onChange={handleChange}
        />

        {localData.asset && (
          <Box textAlign="center" my={3}>
            <img
              src={localData.asset}
              alt="Question Asset"
              style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '10px' }}
            />
          </Box>
        )}

        <TextField
          margin="dense"
          id="keywords"
          label="Keywords"
          type="text"
          fullWidth
          value={localData.keywords || ''}
          onChange={handleChange}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} variant="outlined" color="error">
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          variant="contained"
          color="primary"
          disabled={loading}
        >
          {loading ? <ButtonSpinnerSlot size={24} /> : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
    <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
    </>
  );
};

export default FormDialog;
